<script setup lang="ts">
import { onMounted } from "vue";
import gsap from "gsap";

import avatar0 from "@/assets/docs-assets/avatars/avatar-0.webp";
import avatar1 from "@/assets/docs-assets/avatars/avatar-1.webp";
import avatar2 from "@/assets/docs-assets/avatars/avatar-2.webp";
import avatar3 from "@/assets/docs-assets/avatars/avatar-3.webp";
import avatar4 from "@/assets/docs-assets/avatars/avatar-4.webp";
import avatar5 from "@/assets/docs-assets/avatars/avatar-5.webp";
import avatar6 from "@/assets/docs-assets/avatars/avatar-6.webp";
import avatar7 from "@/assets/docs-assets/avatars/avatar-7.webp";

interface Review {
  name: string;
  username: string;
  body: string;
  img: string;
}

const reviews: Review[] = [
  {
    name: "Alex",
    username: "@alex_gamer",
    body: "The speed of the payouts on this platform is unmatched. Best experience I've had in years.",
    img: avatar0,
  },
  {
    name: "Sarah",
    username: "@sarah_slots",
    body: "I love the new tournament system! Competing against others makes every spin more exciting.",
    img: avatar1,
  },
  {
    name: "Marco",
    username: "@marco_v",
    body: "Smooth gameplay even on mobile. The loyalty rewards keep me coming back daily.",
    img: avatar2,
  },
  {
    name: "Elena",
    username: "@elena_spin",
    body: "The variety of games is incredible. Everything loads instantly, no lag at all.",
    img: avatar3,
  },
  {
    name: "Dimitry",
    username: "@dimitry_777",
    body: "Finally a platform that feels fair and transparent. The GGR transparency is a huge plus.",
    img: avatar4,
  },
  {
    name: "Jin",
    username: "@jin_play",
    body: "User interface is so intuitive. It's the most polished casino app I've used.",
    img: avatar5,
  },
  {
    name: "Chloe",
    username: "@chloe_win",
    body: "I actually feel valued here. The personalized bonuses are always spot on.",
    img: avatar6,
  },
  {
    name: "Leo",
    username: "@leo_bet",
    body: "The live dealer experience is as close to being in Vegas as it gets.",
    img: avatar7,
  },
];

const firstRow = reviews.slice(0, 4);
const secondRow = reviews.slice(4, 8);

onMounted(() => {
  // GSAP animation for smooth infinite scrolling
  const animateRow = (selector: string, direction: number) => {
    const row = document.querySelector(selector) as HTMLElement;
    const items = row.innerHTML;
    row.innerHTML += items + items; // Duplicate for smooth looping

    gsap.to(row, {
      x: direction === 1 ? "-33.333%" : "33.333%",
      duration: 30,
      ease: "none",
      repeat: -1,
      onRepeat: () => {
        gsap.set(row, { x: "0%" });
      }
    });
  };

  animateRow("#row-1", 1);
  animateRow("#row-2", -1);
});
</script>

<template>
  <section class="relative w-full overflow-hidden bg-transparent py-24 select-none">
    <div class="padding-x mb-12">
      <h3 class="heading-1 text-flax-smoke-100 font-bold uppercase leading-none">Players <br/> Love Us /</h3>
      <p class="heading-6 text-flax-smoke-400 mt-4 max-w-[40ch] uppercase">( Proof of Engagement )</p>
    </div>

    <div class="relative flex flex-col gap-6 py-10">
      <!-- Row 1 -->
      <div id="row-1" class="flex w-fit gap-6 whitespace-nowrap px-4">
        <div v-for="(review, i) in [...firstRow, ...firstRow, ...firstRow]" :key="'r1-'+i" 
             class="bg-flax-smoke-900/50 border-flax-smoke-500/20 flex w-[350px] flex-col gap-4 rounded-2xl border p-6 backdrop-blur-sm">
          <div class="flex items-center gap-3">
            <img :src="review.img" class="size-12 rounded-full object-cover border border-purple-500/30" alt="" />
            <div class="flex flex-col">
              <span class="text-flax-smoke-100 text-sm font-bold">{{ review.name }}</span>
              <span class="text-flax-smoke-400 text-xs font-mono">{{ review.username }}</span>
            </div>
          </div>
          <p class="text-flax-smoke-200 text-sm leading-relaxed whitespace-normal italic">"{{ review.body }}"</p>
        </div>
      </div>

      <!-- Row 2 -->
      <div id="row-2" class="flex w-fit gap-6 whitespace-nowrap px-4">
        <div v-for="(review, i) in [...secondRow, ...secondRow, ...secondRow]" :key="'r2-'+i" 
             class="bg-flax-smoke-900/50 border-flax-smoke-500/20 flex w-[350px] flex-col gap-4 rounded-2xl border p-6 backdrop-blur-sm">
          <div class="flex items-center gap-3">
            <img :src="review.img" class="size-12 rounded-full object-cover border border-purple-500/30" alt="" />
            <div class="flex flex-col">
              <span class="text-flax-smoke-100 text-sm font-bold">{{ review.name }}</span>
              <span class="text-flax-smoke-400 text-xs font-mono">{{ review.username }}</span>
            </div>
          </div>
          <p class="text-flax-smoke-200 text-sm leading-relaxed whitespace-normal italic">"{{ review.body }}"</p>
        </div>
      </div>

      <!-- Overlays for fade effect -->
      <div class="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0d0c1d] to-transparent z-10" />
      <div class="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0d0c1d] to-transparent z-10" />
    </div>
  </section>
</template>
